// Host-side handler for the navigator events that the FE iframe relays
// to us via postMessage. The FE-side counterpart lives at
// apps/frontend/src/utils/app-navigator.ts — that file documents the
// protocol. Each ask carries a `requestId` we echo on the response so
// concurrent calls don't race.
//
// Protocol envelope:
//   { key: "ff-<event>:ask" | "ff-<event>:res",
//     v: 1,
//     requestId: string,
//     data?: object }
//
// The `v` field lets us bump message format later without breaking older
// SDK or FE deployments — handlers tolerate missing `v` (treats as v1)
// but emit warnings for unknown future versions.
//
// Why this lives in the SDK: when the FE runs inside an iframe embedded
// on the host site, it can't access the host's URL, native share dialog,
// clipboard, OAuth popups, or top-level navigation from inside the
// iframe (cross-origin policy + permission scoping + Safari ITP). The
// host-side SDK runs in the host's origin and proxies the calls.
//
// Events:
//   - ff-location  — return host window.location (origin/pathname/etc.)
//   - ff-share     — invoke navigator.share on the host
//   - ff-clipboard — invoke navigator.clipboard.writeText on the host
//   - ff-navigate  — set window.location.assign on the host (top-level)
//   - ff-oauth     — open OAuth provider popup from host context, listen
//                    for the popup's auth-success message, relay the
//                    code back down to the iframe. Fixes Safari + iframe
//                    where window.opener is severed by COOP. The popup
//                    posts via the existing `fanfest:async-flow-message`
//                    envelope (so oauth-callback.vue stays unchanged).

import { ASYNC_FLOW_MESSAGE_EVENT_NAME } from "@f3/shared/async-flow-messages";

import type { Logger } from "@/utils/logger";

const PROTOCOL_VERSION = 1;

const events = {
  location: { ask: "ff-location:ask", res: "ff-location:res" },
  share: { ask: "ff-share:ask", res: "ff-share:res" },
  clipboard: { ask: "ff-clipboard:ask", res: "ff-clipboard:res" },
  navigate: { ask: "ff-navigate:ask", res: "ff-navigate:res" },
  oauth: { ask: "ff-oauth:ask", res: "ff-oauth:res" },
} as const;

const ASK_KEYS: ReadonlySet<string> = new Set<string>([
  events.location.ask,
  events.share.ask,
  events.clipboard.ask,
  events.navigate.ask,
  events.oauth.ask,
]);

const OAUTH_POLL_INTERVAL_MS = 500;
const OAUTH_TIMEOUT_MS = 5 * 60 * 1000;
const OAUTH_POPUP_FEATURES =
  "width=500,height=600,scrollbars=yes,resizable=yes,status=yes,location=yes";

// Schemas for the popup → host async-flow envelope. We only consume two
// types: ValidateAuthenticationSuccess (auth code arrived) and
// ValidateAuthenticationError. Other types in the envelope are routed
// elsewhere by the SDK and ignored here.
const ASYNC_VALIDATE_SUCCESS = "validateAuthenticationSuccess" as const;
const ASYNC_VALIDATE_ERROR = "validateAuthenticationError" as const;

interface AskMessage {
  key: string;
  v?: number;
  requestId?: string;
  data?: Record<string, unknown>;
}

interface PendingOauth {
  popup: Window;
  iframeWindow: Window;
  iframeOrigin: string;
  requestId: string;
  pollInterval: ReturnType<typeof setInterval>;
  timeoutHandle: ReturnType<typeof setTimeout>;
}

export class EmbedNavigatorHandler {
  #logger: Logger;
  #embeds: Set<HTMLIFrameElement>;
  #handler?: (event: MessageEvent) => void;
  // Tracks in-flight OAuth flows so we can match the popup's auth-result
  // postMessage back to the iframe that requested it.
  #pendingOauths: Map<string, PendingOauth> = new Map();

  constructor(logger: Logger, embeds: Set<HTMLIFrameElement>) {
    this.#logger = logger.extend("[EmbedNavigatorHandler]");
    this.#embeds = embeds;
  }

  init() {
    if (this.#handler) return;
    this.#handler = (event: MessageEvent) => this.#onMessage(event);
    window.addEventListener("message", this.#handler);
  }

  destroy() {
    if (this.#handler) {
      window.removeEventListener("message", this.#handler);
      this.#handler = undefined;
    }
    // Clean up any in-flight OAuth pollers.
    for (const pending of this.#pendingOauths.values()) {
      clearInterval(pending.pollInterval);
      clearTimeout(pending.timeoutHandle);
      try {
        pending.popup.close();
      } catch {
        /* popup may already be gone */
      }
    }
    this.#pendingOauths.clear();
  }

  #onMessage(event: MessageEvent) {
    const msg = event.data as AskMessage | null;

    // Path A: a ff-* navigator ask from a tracked iframe.
    if (msg && typeof msg.key === "string" && ASK_KEYS.has(msg.key)) {
      this.#handleAsk(msg, event);
      return;
    }

    // Path B: a fanfest:async-flow-message from one of our OAuth popups.
    // The popup posts to its window.opener (= this host page) when the
    // OAuth provider redirects back to oauth-callback.vue.
    if (
      msg &&
      (msg as unknown as { type?: string }).type ===
        ASYNC_FLOW_MESSAGE_EVENT_NAME
    ) {
      this.#handleAsyncFlowFromPopup(event);
      return;
    }
  }

  #handleAsk(msg: AskMessage, event: MessageEvent) {
    if (msg.v !== undefined && msg.v !== PROTOCOL_VERSION) {
      this.#logger.warn("Unknown protocol version on ask; ignoring", {
        key: msg.key,
        v: msg.v,
      });
      return;
    }

    // Only respond to messages from iframes the SDK actually owns. Drops
    // forged messages from any unrelated iframe / window on the host page.
    const source = event.source as Window | null;
    const matchingEl = Array.from(this.#embeds).find(
      (el) => el.contentWindow === source,
    );
    if (!matchingEl || !source) {
      this.#logger.warn("Ignoring ask from unknown source", { key: msg.key });
      return;
    }

    // Pin the iframe origin so subsequent posts target it instead of "*".
    // Falls back to "*" if the iframe URL doesn't expose an origin (e.g.
    // about:blank during early bootstrap).
    let iframeOrigin = "*";
    try {
      iframeOrigin = new URL(matchingEl.src).origin;
    } catch {
      /* leave as "*" */
    }

    void this.#dispatch(msg, source, iframeOrigin);
  }

  async #dispatch(msg: AskMessage, source: Window, iframeOrigin: string) {
    const data = msg.data ?? {};
    const requestId = msg.requestId ?? "";
    try {
      switch (msg.key) {
        case events.location.ask: {
          const loc = window.document.location;
          const queryString = loc.search.startsWith("?")
            ? loc.search.slice(1)
            : loc.search;
          this.#reply(source, iframeOrigin, events.location.res, requestId, {
            ok: true,
            href: loc.href,
            origin: loc.origin,
            pathname: loc.pathname,
            queryString,
          });
          return;
        }
        case events.share.ask: {
          if (
            typeof navigator !== "undefined" &&
            typeof navigator.share === "function"
          ) {
            await navigator.share({
              title: typeof data.title === "string" ? data.title : undefined,
              text: typeof data.text === "string" ? data.text : undefined,
              url: typeof data.url === "string" ? data.url : undefined,
            });
            this.#reply(source, iframeOrigin, events.share.res, requestId, {
              ok: true,
            });
          } else {
            this.#reply(source, iframeOrigin, events.share.res, requestId, {
              ok: false,
              error: "navigator.share unavailable on host",
            });
          }
          return;
        }
        case events.clipboard.ask: {
          const text = typeof data.text === "string" ? data.text : "";
          if (
            typeof navigator !== "undefined" &&
            navigator.clipboard?.writeText
          ) {
            await navigator.clipboard.writeText(text);
            this.#reply(source, iframeOrigin, events.clipboard.res, requestId, {
              ok: true,
            });
          } else {
            this.#reply(source, iframeOrigin, events.clipboard.res, requestId, {
              ok: false,
              error: "navigator.clipboard unavailable on host",
            });
          }
          return;
        }
        case events.navigate.ask: {
          const url = typeof data.url === "string" ? data.url : "";
          if (!url) {
            this.#reply(source, iframeOrigin, events.navigate.res, requestId, {
              ok: false,
              error: "missing url",
            });
            return;
          }
          // Reply BEFORE navigating so the iframe's promise resolves;
          // the host page is about to unload, so ordering matters.
          this.#reply(source, iframeOrigin, events.navigate.res, requestId, {
            ok: true,
          });
          window.location.assign(url);
          return;
        }
        case events.oauth.ask: {
          const authUrl = typeof data.authUrl === "string" ? data.authUrl : "";
          if (!authUrl || !requestId) {
            this.#reply(source, iframeOrigin, events.oauth.res, requestId, {
              ok: false,
              error: "missing authUrl or requestId",
            });
            return;
          }
          this.#startOauth(source, iframeOrigin, requestId, authUrl);
          return;
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      this.#logger.error("Ask handler failed", {
        key: msg.key,
        errorMessage,
      });
      this.#reply(
        source,
        iframeOrigin,
        msg.key.replace(":ask", ":res"),
        requestId,
        { ok: false, error: errorMessage },
      );
    }
  }

  #startOauth(
    iframeWindow: Window,
    iframeOrigin: string,
    requestId: string,
    authUrl: string,
  ) {
    let popup: Window | null = null;
    try {
      popup = window.open(authUrl, "oauth-popup", OAUTH_POPUP_FEATURES);
    } catch (err) {
      this.#logger.warn("OAuth popup open threw", { err });
    }

    if (!popup) {
      this.#reply(iframeWindow, iframeOrigin, events.oauth.res, requestId, {
        ok: false,
        error: "popup blocked",
      });
      return;
    }

    // Periodically check for the popup closing without sending an
    // auth-success message — that's a user cancel. Wrapped in try/catch
    // because Safari throws on cross-origin .closed reads while the
    // popup is on the OAuth provider's origin.
    const popupRef = popup;
    const pollInterval = setInterval(() => {
      let closed = false;
      try {
        closed = popupRef.closed === true;
      } catch {
        closed = false;
      }
      if (closed && this.#pendingOauths.has(requestId)) {
        this.#resolveOauth(requestId, {
          ok: false,
          error: "cancelled",
        });
      }
    }, OAUTH_POLL_INTERVAL_MS);

    // Hard ceiling — five minutes, matches the iframe-side timeout.
    const timeoutHandle = setTimeout(() => {
      if (this.#pendingOauths.has(requestId)) {
        this.#resolveOauth(requestId, {
          ok: false,
          error: "timeout",
        });
      }
    }, OAUTH_TIMEOUT_MS);

    this.#pendingOauths.set(requestId, {
      popup,
      iframeWindow,
      iframeOrigin,
      requestId,
      pollInterval,
      timeoutHandle,
    });
  }

  #handleAsyncFlowFromPopup(event: MessageEvent) {
    // Find the pending OAuth whose popup is the message source.
    let matching: PendingOauth | undefined;
    for (const pending of this.#pendingOauths.values()) {
      if (event.source === pending.popup) {
        matching = pending;
        break;
      }
    }
    if (!matching) return;

    // Validate the inner data shape minimally — we don't import the FE's
    // zod schema here to keep the SDK bundle small. The popup runs our
    // own oauth-callback.vue, so the shape is trusted.
    const inner = (
      event.data as {
        data?: {
          type?: string;
          code?: string;
          state?: string;
          redirect_uri?: string;
          error?: string;
        };
      }
    )?.data;
    if (!inner || typeof inner.type !== "string") return;

    if (
      inner.type === ASYNC_VALIDATE_SUCCESS &&
      typeof inner.code === "string" &&
      typeof inner.state === "string" &&
      typeof inner.redirect_uri === "string"
    ) {
      this.#resolveOauth(matching.requestId, {
        ok: true,
        code: inner.code,
        state: inner.state,
        redirect_uri: inner.redirect_uri,
      });
      return;
    }

    if (inner.type === ASYNC_VALIDATE_ERROR) {
      this.#resolveOauth(matching.requestId, {
        ok: false,
        error: typeof inner.error === "string" ? inner.error : "auth error",
      });
      return;
    }
  }

  #resolveOauth(requestId: string, payload: Record<string, unknown>) {
    const pending = this.#pendingOauths.get(requestId);
    if (!pending) return;
    this.#pendingOauths.delete(requestId);
    clearInterval(pending.pollInterval);
    clearTimeout(pending.timeoutHandle);
    try {
      pending.popup.close();
    } catch {
      /* popup may already be gone */
    }
    this.#reply(
      pending.iframeWindow,
      pending.iframeOrigin,
      events.oauth.res,
      pending.requestId,
      payload,
    );
  }

  #reply(
    source: Window,
    targetOrigin: string,
    resKey: string,
    requestId: string,
    data: Record<string, unknown>,
  ) {
    try {
      source.postMessage(
        { key: resKey, v: PROTOCOL_VERSION, requestId, data },
        targetOrigin,
      );
    } catch (err) {
      this.#logger.error("Failed to post reply", { resKey, err });
    }
  }
}
